// PO7-S1 (docs/build-briefs/PO7-S1-invoice-attach.md; ADR-013 Slice S1).
// The "Invoice" card, built once and mounted in two places: the receiving
// screen and the purchase order detail. It attaches an invoice file (a
// camera capture or a file picker), lists what is attached, and opens or
// downloads a file. Touch only: two large buttons, no drag and drop, no
// hover, no tooltip. It decides only what to SHOW; every request is checked
// by the server's own permission and tenant scope.
//
// Attach needs inv.receipt.record; the list and the files need
// inv.receipt.view (the same permissions the routes require). A session
// holding neither sees no card at all.
//
// PO7-S3: a draft (View draft) and the review (Review invoice) show cost
// figures, so they are offered only to a session that also holds inv.cost.view,
// the same pair the routes require. Changing a line needs inv.receipt.record
// as well.
//
// PO7-S4: Confirm invoice needs purchasing.invoice.confirm as well as
// inv.cost.view (a person who cannot see costs cannot confirm them); the screen
// only decides whether to offer the button, the server decides.

import {
  getInvoiceAttachmentFile,
  getInvoiceDraft,
  getInvoiceReview,
  listInvoiceAttachments,
  postInvoiceConfirm,
  postInvoiceReviewAction,
  retryInvoiceReading,
  uploadInvoiceAttachment,
  type InvoiceAttachmentDto,
  type InvoiceDraftDto,
} from "./api.ts";
import {
  checkInvoiceFileBeforeUpload,
  describeInvoiceDuplicate,
  describeInvoiceUpload,
  formatInvoiceDate,
  INVOICE_FILE_ACCEPT,
} from "./invoiceAttachmentsDisplay.ts";
import {
  describeDraftDuplicate,
  describeReading,
  formatConfidence,
  formatDraftMoney,
  readingAction,
} from "./invoiceDraftDisplay.ts";
import {
  renderInvoiceReview,
  type ConfirmResult,
  type PendingLock,
  type ReviewActionResult,
} from "./invoiceReviewView.ts";
import { hasPermission, type Session } from "./session.ts";

const ATTACH_PERMISSION = "inv.receipt.record";
const VIEW_PERMISSION = "inv.receipt.view";
const COST_PERMISSION = "inv.cost.view";
const CONFIRM_PERMISSION = "purchasing.invoice.confirm";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (testId !== undefined) {
    node.dataset["testid"] = testId;
  }
  return node;
}

/** Fills `host` with the invoice card for one order. `testPrefix` keeps the
 * data-testid values of the two mounts distinct. Safe to call again for the
 * same host (it rebuilds); a slower earlier call for another order never
 * paints over a newer one. */
export async function mountInvoiceCard(
  host: HTMLElement,
  orderId: string,
  testPrefix: string,
  getSession: () => Session,
  /** CORE7-F2: called after a confirm with the purchase order lines it confirmed. */
  onConfirmed?: (orderId: string, purchaseOrderLineIds: readonly string[]) => void,
): Promise<void> {
  host.dataset["invoiceOrderId"] = orderId;
  host.replaceChildren();
  const session = getSession();
  const canAttach = hasPermission(session, ATTACH_PERMISSION);
  const canView = hasPermission(session, VIEW_PERMISSION);
  const canSeeCosts = canView && hasPermission(session, COST_PERMISSION);
  const canConfirm = canSeeCosts && hasPermission(session, CONFIRM_PERMISSION);
  host.hidden = !(canAttach || canView);
  if (host.hidden) {
    return;
  }
  const t = (name: string): string => `${testPrefix}-invoice-${name}`;
  const isCurrent = (): boolean => host.dataset["invoiceOrderId"] === orderId;

  const card = el("div", "invoice-card", t("card"));
  const title = el("h2", "section-title");
  title.textContent = "Invoice";
  const help = el("p", "admin-field-help");
  help.textContent = canAttach
    ? "Attach the vendor's invoice for this delivery. Files are kept with this order."
    : "Invoice files attached to this order.";
  const errorLine = el("p", "admin-error invoice-error", t("error"));
  errorLine.hidden = true;
  errorLine.setAttribute("role", "alert");
  const statusLine = el("p", "admin-field-help invoice-status", t("status"));
  statusLine.hidden = true;
  statusLine.setAttribute("role", "status");
  const list = el("div", "invoice-list", t("list"));
  const reviewHost = el("div", "invoice-review-host", t("review-host"));
  reviewHost.hidden = true;
  card.append(title, help);

  const showError = (message: string): void => {
    errorLine.textContent = message;
    errorLine.hidden = false;
  };
  const clearError = (): void => {
    errorLine.textContent = "";
    errorLine.hidden = true;
    statusLine.textContent = "";
    statusLine.hidden = true;
  };
  const showAttached = (names: string[]): void => {
    statusLine.textContent = `Attached ${names.join(", ")}.`;
    statusLine.hidden = false;
  };

  const captureButtons: HTMLElement[] = [];
  let captureActions: HTMLElement | null = null;
  if (canAttach) {
    const actions = el("div", "invoice-capture-actions");
    const makePicker = (
      label: string,
      inputTestId: string,
      configure: (input: HTMLInputElement) => void,
    ): void => {
      const wrap = el("label", "btn-secondary invoice-capture-button");
      const text = document.createElement("span");
      text.textContent = label;
      const input = el("input", "invoice-file-input", inputTestId);
      input.type = "file";
      configure(input);
      input.addEventListener("change", () => {
        const files = Array.from(input.files ?? []);
        input.value = "";
        void attachFiles(files);
      });
      wrap.append(text, input);
      actions.append(wrap);
      captureButtons.push(wrap);
    };
    makePicker("Take photo", t("photo-input"), (input) => {
      input.accept = "image/*";
      input.setAttribute("capture", "environment");
    });
    makePicker("Upload file", t("file-input"), (input) => {
      input.accept = INVOICE_FILE_ACCEPT;
      input.multiple = true;
    });
    card.append(actions);
    captureActions = actions;
  }
  card.append(errorLine, statusLine, list, reviewHost);
  host.append(card);

  const setBusy = (busy: boolean): void => {
    for (const button of captureButtons) {
      button.classList.toggle("invoice-capture-button-busy", busy);
      const input = button.querySelector("input");
      if (input !== null) {
        input.disabled = busy;
      }
    }
  };

  const attachFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) {
      return;
    }
    clearError();
    setBusy(true);
    const attached: string[] = [];
    try {
      for (const file of files) {
        const check = checkInvoiceFileBeforeUpload(file);
        if (!check.ok) {
          showError(`${file.name}: ${check.message}`);
          continue;
        }
        // One key per file picked. A dropped connection retries once with
        // the SAME key, so a request that had already committed is
        // returned, never stored twice.
        const key = crypto.randomUUID();
        const current = getSession();
        let result = await uploadInvoiceAttachment(
          current.apiBaseUrl,
          current.tenantId,
          orderId,
          file,
          check.contentType,
          key,
        );
        if (result.networkError) {
          result = await uploadInvoiceAttachment(
            current.apiBaseUrl,
            current.tenantId,
            orderId,
            file,
            check.contentType,
            key,
          );
        }
        if (result.networkError) {
          showError(`${file.name}: could not upload. Check your connection and try again.`);
        } else if (result.status !== 200 && result.status !== 201) {
          showError(`${file.name}: ${result.body?.message ?? "could not attach this file."}`);
        } else {
          attached.push(file.name);
        }
      }
    } finally {
      setBusy(false);
    }
    if (attached.length > 0) {
      showAttached(attached);
    }
    if (isCurrent() && canView) {
      await refreshList();
    }
  };

  const buildRow = (invoice: InvoiceAttachmentDto): HTMLElement => {
    const row = el("div", "invoice-row", t("row"));
    row.dataset["attachmentId"] = invoice.id;
    const thumb = el("div", "invoice-thumb");
    thumb.setAttribute("aria-hidden", "true");
    thumb.textContent = invoice.contentType === "application/pdf" ? "PDF" : "IMG";
    const main = el("div", "invoice-row-main");
    const name = el("div", "invoice-file-name", t("name"));
    name.textContent = invoice.originalFilename;
    const meta = el("div", "invoice-file-meta", t("meta"));
    meta.textContent = describeInvoiceUpload(invoice, new Date());
    main.append(name, meta);
    const actions = el("div", "invoice-row-actions");
    const open = el("button", "btn-secondary invoice-action-button", t("open"));
    open.type = "button";
    open.textContent = "Open";
    const download = el("button", "btn-secondary invoice-action-button", t("download"));
    download.type = "button";
    download.textContent = "Download";
    open.addEventListener("click", () => void openFile(invoice, "inline"));
    download.addEventListener("click", () => void openFile(invoice, "attachment"));
    actions.append(open, download);
    row.append(thumb, main, actions);
    const wrapper = el("div", "invoice-row-group", t("group"));
    wrapper.append(row);
    if (invoice.duplicateOf !== null) {
      const text = describeInvoiceDuplicate(invoice.duplicateOf);
      const warning = el("div", "invoice-dup-warning", t("duplicate-warning"));
      warning.setAttribute("role", "status");
      const strong = document.createElement("strong");
      strong.textContent = text.title;
      warning.append(strong, document.createTextNode(text.detail));
      wrapper.append(warning);
    }
    wrapper.append(buildReading(invoice));
    return wrapper;
  };

  // PO7-S2. The reading line under a file: where the reading stands, and the
  // one action that status offers. A draft opens inline, read-only.
  const buildReading = (invoice: InvoiceAttachmentDto): HTMLElement => {
    const reading = invoice.reading;
    const block = el("div", "invoice-reading", t("reading-block"));
    const line = el("div", "invoice-reading-line");
    const status = el(
      "span",
      `invoice-reading-status invoice-reading-${reading.status}`,
      t("reading"),
    );
    status.textContent = describeReading(reading.status);
    line.append(status);
    const action = readingAction(reading.status, { canRetry: canAttach });
    const drafts = el("div", "invoice-draft-host");
    if (action === "view" && canSeeCosts) {
      const view = el("button", "btn-secondary invoice-action-button", t("draft-view"));
      view.type = "button";
      view.textContent = "View draft";
      view.setAttribute("aria-expanded", "false");
      view.addEventListener("click", () => {
        if (!drafts.hidden && drafts.childElementCount > 0) {
          drafts.replaceChildren();
          drafts.hidden = true;
          view.textContent = "View draft";
          view.setAttribute("aria-expanded", "false");
          return;
        }
        void showDraft(invoice, drafts, view);
      });
      line.append(view);
      const review = el("button", "btn-secondary invoice-action-button", t("review-open"));
      review.type = "button";
      review.textContent = "Review invoice";
      review.addEventListener("click", () => void openReview(invoice, review));
      line.append(review);
    } else if (action === "retry") {
      const retry = el("button", "btn-secondary invoice-action-button", t("reading-retry"));
      retry.type = "button";
      retry.textContent = "Try again";
      retry.addEventListener("click", () => void requestRetry(invoice, retry));
      line.append(retry);
    }
    drafts.hidden = true;
    block.append(line, drafts);
    return block;
  };

  const requestRetry = async (
    invoice: InvoiceAttachmentDto,
    button: HTMLButtonElement,
  ): Promise<void> => {
    clearError();
    button.disabled = true;
    const current = getSession();
    const result = await retryInvoiceReading(
      current.apiBaseUrl,
      current.tenantId,
      orderId,
      invoice.id,
    );
    if (result.networkError) {
      button.disabled = false;
      showError("Could not ask for another reading. Check your connection.");
      return;
    }
    if (result.status !== 202) {
      button.disabled = false;
      showError(result.body?.message ?? "Could not ask for another reading.");
    }
    if (isCurrent() && canView) {
      await refreshList();
    }
  };

  // PO7-S3. The review replaces the card's list while it is open, as the
  // mockup's screen replaces the receiving page, and "Back to invoices" brings
  // the list back. Every figure on it is read from the server again after each
  // action, so what is shown is what was recorded.
  const setReviewing = (reviewing: boolean): void => {
    list.hidden = reviewing;
    help.hidden = reviewing;
    title.hidden = reviewing;
    if (captureActions !== null) {
      captureActions.hidden = reviewing;
    }
    reviewHost.hidden = !reviewing;
    if (!reviewing) {
      reviewHost.replaceChildren();
    }
  };

  const closeReview = async (): Promise<void> => {
    setReviewing(false);
    if (isCurrent() && canView) {
      await refreshList();
    }
  };

  const paintReview = (
    review: Parameters<typeof renderInvoiceReview>[0]["review"],
    pendingLock?: PendingLock,
  ): void => {
    reviewHost.replaceChildren(
      renderInvoiceReview({
        review,
        testPrefix: `${testPrefix}-invoice`,
        canAct: canAttach && canSeeCosts,
        canConfirm,
        pendingLock,
        onBack: () => void closeReview(),
        onUpdated: paintReview,
        onConfirm: async (token, key): Promise<ConfirmResult> => {
          // One key for this confirm; a dropped connection retries once with the
          // SAME key, so a request that had already committed is returned, never
          // recorded twice.
          const current = getSession();
          const send = (): ReturnType<typeof postInvoiceConfirm> =>
            postInvoiceConfirm(
              current.apiBaseUrl,
              current.tenantId,
              orderId,
              review.attachmentId,
              token,
              key,
            );
          let result = await send();
          if (result.networkError) {
            result = await send();
          }
          if (result.networkError) {
            return {
              ok: false,
              message: "Could not confirm. Check your connection and try again.",
            };
          }
          if (
            (result.status !== 200 && result.status !== 201) ||
            result.body.review === undefined ||
            result.body.retention === undefined
          ) {
            return { ok: false, message: result.body?.message ?? "Could not confirm the invoice." };
          }
          onConfirmed?.(
            orderId,
            (result.body.review.confirmed?.lines ?? []).map((line) => line.purchaseOrderLineId),
          );
          return { ok: true, review: result.body.review, retention: result.body.retention };
        },
        onAction: async (lineNumber, body): Promise<ReviewActionResult> => {
          // One key for this tap; a dropped connection retries once with the
          // SAME key, so a request that had already committed is returned,
          // never recorded twice.
          const key = crypto.randomUUID();
          const current = getSession();
          const send = (): ReturnType<typeof postInvoiceReviewAction> =>
            postInvoiceReviewAction(
              current.apiBaseUrl,
              current.tenantId,
              orderId,
              review.attachmentId,
              lineNumber,
              body,
              key,
            );
          let result = await send();
          if (result.networkError) {
            result = await send();
          }
          if (result.networkError) {
            return {
              ok: false,
              message: "Could not save that. Check your connection and try again.",
            };
          }
          if (
            (result.status !== 200 && result.status !== 201) ||
            result.body.review === undefined
          ) {
            return { ok: false, message: result.body?.message ?? "Could not save that change." };
          }
          return { ok: true, review: result.body.review };
        },
      }),
    );
  };

  const openReview = async (
    invoice: InvoiceAttachmentDto,
    button: HTMLButtonElement,
  ): Promise<void> => {
    clearError();
    button.disabled = true;
    const current = getSession();
    const result = await getInvoiceReview(
      current.apiBaseUrl,
      current.tenantId,
      orderId,
      invoice.id,
    );
    button.disabled = false;
    if (result.status !== 200 || result.body.review === undefined) {
      showError(
        result.networkError
          ? "Could not open the review. Check your connection."
          : (result.body?.message ?? "Could not open the review."),
      );
      return;
    }
    setReviewing(true);
    paintReview(result.body.review);
    reviewHost.scrollIntoView({ block: "start" });
  };

  const showDraft = async (
    invoice: InvoiceAttachmentDto,
    host: HTMLElement,
    toggle: HTMLButtonElement,
  ): Promise<void> => {
    clearError();
    const current = getSession();
    const result = await getInvoiceDraft(current.apiBaseUrl, current.tenantId, orderId, invoice.id);
    if (result.status !== 200 || result.body.draft === undefined) {
      showError(
        result.networkError
          ? "Could not open the draft. Check your connection."
          : (result.body?.message ?? "Could not open the draft."),
      );
      return;
    }
    host.replaceChildren(renderDraft(result.body.draft));
    host.hidden = false;
    toggle.textContent = "Hide draft";
    toggle.setAttribute("aria-expanded", "true");
  };

  /** The read-only draft: text only, no inputs, no buttons, nothing to confirm. */
  const renderDraft = (draft: InvoiceDraftDto): HTMLElement => {
    const panel = el("div", "invoice-draft", t("draft"));
    const note = el("p", "invoice-draft-note", t("draft-note"));
    note.textContent = "Draft, not confirmed. Nothing has been posted.";
    panel.append(note);

    if (draft.duplicateInvoiceNumber !== null) {
      const text = describeDraftDuplicate(draft.invoiceNumber.value, draft.duplicateInvoiceNumber);
      const banner = el("div", "invoice-dup-warning", t("draft-duplicate"));
      banner.setAttribute("role", "status");
      const strong = document.createElement("strong");
      strong.textContent = text.title;
      banner.append(strong, document.createTextNode(text.detail));
      panel.append(banner);
    }

    const facts = el("dl", "invoice-draft-facts");
    const addFact = (label: string, value: string, confidence: number | null, id: string): void => {
      const term = document.createElement("dt");
      term.textContent = label;
      const detail = document.createElement("dd");
      const text = el("span", "invoice-draft-value", t(id));
      text.textContent = value;
      const conf = el("span", "invoice-draft-confidence");
      conf.textContent = `Read at ${formatConfidence(confidence)}`;
      detail.append(text, conf);
      facts.append(term, detail);
    };
    const dateText = (iso: string | null): string =>
      iso === null ? "Not read" : formatInvoiceDate(iso);
    addFact(
      "Vendor",
      draft.vendorName.value ?? "Not read",
      draft.vendorName.confidence,
      "draft-vendor",
    );
    addFact(
      "Invoice number",
      draft.invoiceNumber.value ?? "Not read",
      draft.invoiceNumber.confidence,
      "draft-number",
    );
    addFact(
      "Invoice date",
      dateText(draft.invoiceDate.value),
      draft.invoiceDate.confidence,
      "draft-date",
    );
    addFact("Due date", dateText(draft.dueDate.value), draft.dueDate.confidence, "draft-due");
    panel.append(facts);

    const table = el("table", "invoice-draft-lines", t("draft-lines"));
    const head = document.createElement("thead");
    const headRow = document.createElement("tr");
    for (const label of ["Item", "Qty", "Unit cost", "Line total", "Read at"]) {
      const th = document.createElement("th");
      th.scope = "col";
      th.textContent = label;
      headRow.append(th);
    }
    head.append(headRow);
    const body = document.createElement("tbody");
    for (const line of draft.lines) {
      const tr = el("tr", "invoice-draft-line", t("draft-line"));
      const item = document.createElement("td");
      const code = line.vendorItemCode === null ? "" : `${line.vendorItemCode} `;
      item.textContent = `${code}${line.description ?? ""}`.trim() || "Not read";
      const qty = document.createElement("td");
      qty.textContent = line.quantity ?? "Not read";
      const unit = document.createElement("td");
      unit.textContent = formatDraftMoney(line.unitPriceCents);
      const total = document.createElement("td");
      total.textContent = formatDraftMoney(line.lineTotalCents);
      const conf = document.createElement("td");
      conf.textContent = formatConfidence(line.confidence);
      tr.append(item, qty, unit, total, conf);
      body.append(tr);
    }
    if (draft.lines.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 5;
      td.textContent = "No lines were read from this invoice.";
      tr.append(td);
      body.append(tr);
    }
    table.append(head, body);
    panel.append(table);

    const totals = el("dl", "invoice-draft-totals");
    const addTotal = (label: string, cents: number | null, id: string): void => {
      const term = document.createElement("dt");
      term.textContent = label;
      const detail = el("dd", "invoice-draft-total-value", t(id));
      detail.textContent = formatDraftMoney(cents);
      totals.append(term, detail);
    };
    addTotal("Subtotal", draft.subtotalCents.value, "draft-subtotal");
    addTotal("Tax", draft.taxCents.value, "draft-tax");
    addTotal("Total", draft.totalCents.value, "draft-total");
    panel.append(totals);
    return panel;
  };

  const openFile = async (
    invoice: InvoiceAttachmentDto,
    disposition: "inline" | "attachment",
  ): Promise<void> => {
    clearError();
    // SEC9: never a blob: tab. A blob tab inherits the page's Content-Security-Policy (object-src 'none'), which can leave
    // Chrome's PDF viewer blank, so both buttons hand the file to the browser as a download.
    const current = getSession();
    const result = await getInvoiceAttachmentFile(
      current.apiBaseUrl,
      current.tenantId,
      orderId,
      invoice.id,
      disposition,
    );
    if (result.status !== 200 || result.blob === undefined) {
      showError(
        result.networkError
          ? "Could not open this file. Check your connection."
          : (result.message ?? "Could not open this file."),
      );
      return;
    }
    const url = URL.createObjectURL(result.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = invoice.originalFilename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const refreshList = async (): Promise<void> => {
    const current = getSession();
    const result = await listInvoiceAttachments(current.apiBaseUrl, current.tenantId, orderId);
    if (!isCurrent()) {
      return;
    }
    list.replaceChildren();
    if (result.status !== 200 || result.body.invoices === undefined) {
      showError(
        result.body?.message ?? "Could not load the attached invoices. Check your connection.",
      );
      return;
    }
    if (result.body.invoices.length === 0) {
      const empty = el("p", "invoice-empty", t("empty"));
      empty.textContent = "No invoice attached yet.";
      list.append(empty);
      return;
    }
    for (const invoice of result.body.invoices) {
      list.append(buildRow(invoice));
    }
  };

  if (canView) {
    await refreshList();
  }
}
